import { useAnalytics } from '@contexts/AnalyticsContext'
import { MembershipKinds } from '@models/membership/MembershipAnalyticsStatistics'
import { ProductType } from '@models/Product'
import { UserKinds } from '@models/user/UserAnalyticsStatistics'
import { VenueKinds } from '@models/venue/VenueAnalyticsStatistics'
import { useEffect, useState } from 'react'

interface BaseFetchParams {
  entity: 'user' | 'venue' | 'membership'
  entityId?: number
  kind: UserKinds | VenueKinds | MembershipKinds
  startDate: string
  endDate: string
  product?: ProductType
  programIds?: number[]
}

interface Props<TParams extends BaseFetchParams, TResponse> {
  entityId: number | undefined
  kind: UserKinds | VenueKinds | MembershipKinds
  entity: 'user' | 'venue' | 'membership'
  fetchFunction: (params: TParams) => Promise<{ data: TResponse }>
}

const useFetchAnalyticsStatistics = <
  TParams extends BaseFetchParams,
  TResponse,
>({
  entityId,
  kind,
  entity,
  fetchFunction,
}: Props<TParams, TResponse>) => {
  const {
    selectedDates,
    selectedProduct,
    selectedMembershipPrograms,
    isMembershipProgramReady,
  } = useAnalytics()

  const [data, setData] = useState<TResponse | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()

  const fetchData = async () => {
    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setError(selectedDates && 'No data available.')
      setData(undefined)
      setIsLoading(false)
      return
    }

    const fetchParams = {
      entity,
      entityId,
      kind,
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      product: selectedProduct,
      programIds: selectedMembershipPrograms,
    } as TParams

    try {
      const response = await fetchFunction(fetchParams)

      if (!response.data) {
        setError('No data available.')
        return
      }

      setData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('Failed to fetch data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Waiting on the program scope avoids a first, unscoped request that a
    // second one would immediately contradict once the default program lands.
    if (!selectedDates || !isMembershipProgramReady) return

    fetchData()
  }, [
    selectedDates,
    selectedProduct,
    selectedMembershipPrograms,
    isMembershipProgramReady,
    entityId,
  ])

  return { data, isLoading, error }
}

export default useFetchAnalyticsStatistics
